import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowDownToLine } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const statusColor = {
  pending: "bg-amber-500/10 text-amber-400",
  approved: "bg-emerald-500/10 text-emerald-400",
  rejected: "bg-red-500/10 text-red-400",
};

export default function Withdraw() {
  const [balance, setBalance] = useState(0);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ amount: "", network: "TRC20", to_address: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [dash, s, wd] = await Promise.all([
      api.get("/dashboard"), api.get("/settings"), api.get("/withdrawals"),
    ]);
    setBalance(dash.data.balance.available);
    setSettings(s.data.settings);
    setRows(wd.data.withdrawals);
  };
  useEffect(() => { load(); }, []);

  const amount = parseFloat(form.amount) || 0;
  const fee = (amount * (settings.withdrawal_fee_pct || 0)) / 100;
  const net = amount - fee;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdraw", { ...form, amount });
      toast.success("Withdrawal request submitted! It will be processed after admin approval.");
      setForm({ amount: "", network: "TRC20", to_address: "" });
      load();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-1">Withdraw USDT</h1>
      <p className="text-sm text-slate-400 mb-6">Available: <span className="text-emerald-400 font-mono font-semibold">${fmt(balance)}</span></p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface rounded-2xl p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Network</Label>
              <div className="flex gap-2 mt-1.5">
                {["TRC20", "BEP20"].map((n) => (
                  <button key={n} type="button" data-testid={`withdraw-net-${n.toLowerCase()}`}
                    onClick={() => setForm({ ...form, network: n })}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${form.network === n ? "bg-emerald-500 text-white" : "bg-white/5 text-slate-400"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Your USDT Wallet Address</Label>
              <Input data-testid="withdraw-address-input" required value={form.to_address}
                onChange={(e) => setForm({ ...form, to_address: e.target.value })}
                className="mt-1.5 bg-white/5 border-slate-700 font-mono" placeholder="Recipient address" />
            </div>
            <div>
              <Label className="text-slate-300">Amount (USDT)</Label>
              <Input data-testid="withdraw-amount-input" type="number" required value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1.5 bg-white/5 border-slate-700" placeholder={`Min ${settings.min_withdrawal || 10}`} />
            </div>
            <div className="rounded-xl bg-white/5 p-4 text-sm space-y-1">
              <div className="flex justify-between text-slate-400"><span>Fee ({settings.withdrawal_fee_pct || 0}%)</span><span className="font-mono">${fmt(fee)}</span></div>
              <div className="flex justify-between font-semibold"><span>You receive</span><span className="font-mono text-emerald-400" data-testid="withdraw-net-display">${fmt(net > 0 ? net : 0)}</span></div>
            </div>
            <Button data-testid="withdraw-submit-button" type="submit" disabled={busy}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white h-11">
              <ArrowDownToLine className="h-4 w-4 mr-2" /> {busy ? "Submitting…" : "Request Withdrawal"}
            </Button>
          </form>
        </div>

        <div className="card-surface rounded-2xl p-6">
          <h3 className="font-semibold mb-4">Withdrawal History</h3>
          {rows.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="no-withdrawals">No withdrawal requests yet.</div>
          ) : (
            <div className="space-y-3">
              {rows.map((w) => (
                <div key={w.id} className="border border-border rounded-xl p-3" data-testid={`withdrawal-${w.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-semibold">${fmt(w.amount)} <span className="text-xs text-slate-500">{w.network}</span></span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[w.status]}`}>{w.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 break-all">To: {w.to_address}</div>
                  {w.tx_hash && <div className="text-xs text-emerald-400 break-all mt-1">TXID: {w.tx_hash}</div>}
                  {w.reason && <div className="text-xs text-red-400 mt-1">Reason: {w.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
